import { Grid, Box, Button, Typography, FormHelperText } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { ErrorCode, FileRejection, useDropzone } from 'react-dropzone';
import { companyIdAtom } from '../../state';
import { ProductPackagingsFormData } from '../../ProductFormPackagings';
import {
  SUPPORTED_IMAGE_ACCEPT,
  MAXIMUM_FILE_SIZE_IN_BYTES,
  useUploadFile,
} from '../../../../hooks/useUploadFile';
import FileUtils, {
  FileParameters,
  getFileFormatLabel,
  SUPPORTED_IMAGE_FORMATS_LABEL,
} from '../../../../common/utils/file.utils';
import GridPackagingPicture from './GridPackagingPicture';

const MAX_PHOTOS = 6;

export default function BaseUnitPhotosForm({ csuIndex }: { csuIndex: number }): React.JSX.Element {
  const { t, i18n } = useTranslation(['products', 'error']);
  const { register, setValue, watch, getValues } = useFormContext<ProductPackagingsFormData>();
  const photos = watch(`productPackagings.${csuIndex}.csu.photos`);
  const mainPhoto = watch(`productPackagings.${csuIndex}.csu.main_photo`);
  const companyId = useAtomValue(companyIdAtom);
  const [editPictureIndex, setEditPictureIndex] = useState<number | undefined>();
  const [fileErrorMessage, setFileErrorMessage] = useState<string>();
  const {
    handleFileChange,
    isFileUploadLoading,
  } = useUploadFile();

  const getFileRejectionErrorMessage = (rejectedFiles: FileRejection[]): string | undefined => {
    if (!rejectedFiles.length) {
      return undefined;
    }

    if (rejectedFiles.length > 1) {
      return t('products:pictures_some_files_rejected', {
        acceptedFormats: SUPPORTED_IMAGE_FORMATS_LABEL,
      });
    }

    const errorCode = rejectedFiles[0].errors[0].code as ErrorCode;

    switch (errorCode) {
      case ErrorCode.TooManyFiles:
        return t('products:pictures_max_count', { count: MAX_PHOTOS });
      case ErrorCode.FileTooLarge:
        return t('error:file_too_large', {
          size: FileUtils.FormatFileSizeLimitLabel(MAXIMUM_FILE_SIZE_IN_BYTES, i18n.language),
        });
      case ErrorCode.FileInvalidType: {
        const rejectedFile = rejectedFiles[0].file;
        const fileFormat = getFileFormatLabel(rejectedFile);

        if (fileFormat) {
          return t('error:invalid_file_type_with_provided_format', {
            fileFormat,
            acceptedFormats: SUPPORTED_IMAGE_FORMATS_LABEL,
          });
        }

        return t('error:invalid_file_type', {
          acceptedFormats: SUPPORTED_IMAGE_FORMATS_LABEL,
        });
      }
      default:
        return t('products:pictures_some_files_rejected', {
          acceptedFormats: SUPPORTED_IMAGE_FORMATS_LABEL,
        });
    }
  };

  const updateForm = (fileParameters: FileParameters): void => {
    if (fileParameters) {
      const currentPhotos = getValues(`productPackagings.${csuIndex}.csu.photos`) ?? [];
      const currentMainPhoto = getValues(`productPackagings.${csuIndex}.csu.main_photo`);
      const newPhotos = [...currentPhotos];

      if (editPictureIndex !== undefined) {
        const editedPhotoWasMainPhoto = newPhotos[editPictureIndex].path === currentMainPhoto?.path;

        if (editedPhotoWasMainPhoto) {
          setValue(`productPackagings.${csuIndex}.csu.main_photo`, fileParameters);
        }
        newPhotos[editPictureIndex] = fileParameters;
        setEditPictureIndex(undefined);
      } else {
        if (newPhotos.length === 0) {
          // when there are not any photos, make sure the first inserted is set as main photo
          setValue(`productPackagings.${csuIndex}.csu.main_photo`, fileParameters);
        }
        newPhotos.push(fileParameters);
      }

      setValue(`productPackagings.${csuIndex}.csu.photos`, newPhotos, { shouldDirty: true });
    }
  };

  const onDrop = async (acceptedFiles: File[], rejectedFiles: FileRejection[]): Promise<void> => {
    setFileErrorMessage(getFileRejectionErrorMessage(rejectedFiles));

    if (!acceptedFiles.length) {
      return;
    }

    if (editPictureIndex !== undefined) {
      const fileParams = await handleFileChange(acceptedFiles[0], 'PACKAGING_PHOTOS', companyId);
      updateForm(fileParams);
      return;
    }

    const slotsLeft = MAX_PHOTOS - photos.length;
    for (const file of acceptedFiles.slice(0, slotsLeft)) {
      // eslint-disable-next-line no-await-in-loop
      const fileParams = await handleFileChange(file, 'PACKAGING_PHOTOS', companyId);
      updateForm(fileParams);
    }
  };

  const slotsLeft = Math.max(0, MAX_PHOTOS - photos.length);

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    accept: SUPPORTED_IMAGE_ACCEPT,
    multiple: editPictureIndex === undefined,
    maxFiles: editPictureIndex !== undefined ? 1 : slotsLeft,
    maxSize: MAXIMUM_FILE_SIZE_IN_BYTES,
    noClick: true,
    noKeyboard: true,
  });

  const handleEditPictureClick = (pictureIndex: number): void => {
    setEditPictureIndex(pictureIndex);
    open();
  };

  const handleDeletePictureClick = (pictureIndex: number): void => {
    const deletedPhotoWasMain = photos[pictureIndex].path === mainPhoto?.path;
    const newPhotos = photos.filter((photo, index) => index !== pictureIndex);

    setValue(`productPackagings.${csuIndex}.csu.photos`, newPhotos, { shouldDirty: true });
    if (deletedPhotoWasMain) {
      setValue(`productPackagings.${csuIndex}.csu.main_photo`, newPhotos[0]);
    }
  };

  return (
    <Box>
      <Typography variant="titleLarge" sx={{ mb: 1 }}>
        {t('products:pictures_other')}
      </Typography>
      <Typography variant="bodyMedium" color="secondary">
        {t('products:pictures_form_description')}
      </Typography>
      <Box
        {...getRootProps()}
        sx={{
          mt: 3,
          p: 1,
          borderRadius: 4,
          border: '1px dashed',
          borderColor: isDragActive ? 'primary.main' : 'outlineVariant.main',
          backgroundColor: isDragActive ? 'surfaceContainerLow.main' : 'transparent',
          transition: 'border-color 0.2s, background-color 0.2s',
        }}
      >
        <input {...getInputProps()} />
        <Grid
          container
          rowSpacing={1}
          columnSpacing={3}
          sx={{
            minHeight: 194,
            justifyContent: 'center',
          }}
        >
          {
            !photos?.length
              ? (
                <Grid size={{ xs: 12 }} sx={{ textAlign: 'center', my: 'auto' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
                      <Typography variant="bodyMedium">
                        {t('products:drag_and_drop_pictures_or_click')}
                      </Typography>
                      <Typography variant="bodySmall" color="secondary">
                        {t('products:pictures_format_description')}
                      </Typography>
                    </Box>
                    <Button
                      loading={isFileUploadLoading}
                      variant="outlined"
                      onClick={open}
                      startIcon={<AddIcon />}
                    >
                      {t('products:add_pictures')}
                    </Button>
                  </Box>
                </Grid>
              )
              : (
                <>
                  {photos.map((photo, index) => (
                    <GridPackagingPicture
                      key={index}
                      src={photo}
                      csuIndex={csuIndex}
                      onEdit={() => handleEditPictureClick(index)}
                      onDelete={() => handleDeletePictureClick(index)}
                    />
                  ))}
                  {photos.length < MAX_PHOTOS && (
                    <Grid size={{ xs: 'auto' }} sx={{ my: 'auto' }}>
                      <Button
                        loading={isFileUploadLoading}
                        variant="outlined"
                        onClick={open}
                      >
                        <AddIcon />
                      </Button>
                    </Grid>
                  )}
                </>
              )
          }
        </Grid>
        {fileErrorMessage && (
          <FormHelperText error sx={{ textAlign: 'center', mt: 1 }}>
            {fileErrorMessage}
          </FormHelperText>
        )}
      </Box>
      <input type="hidden" {...register(`productPackagings.${csuIndex}.csu.photos`)} />
    </Box>
  );
}
